/**
 * Rules behind the app-version floors screen.
 *
 * The floors in `app_version_policies` reach every installed copy of the mobile apps: the
 * minimum blocks an app outright, the recommended one raises a dismissible nudge. The server is
 * the authority on the policy and each client evaluates it (see condo-manager's `appversion`
 * package and the shared fixture testing/appversion/gate_cases.json) — this module is only the
 * admin surface for editing it.
 */

/** Whatever the token gave us. Keycloak may populate either field. */
export interface AppVersionAdminIdentity {
  username?: string | null;
  email?: string | null;
}

/**
 * Hardcoded to one person, by request.
 *
 * Not a role: a role is something that can be granted by accident, and the blast radius here is
 * every phone in the fleet — a wrong minimum_version locks out every porteiro at every gate at
 * once. The backend keeps its own guard (`super_admin`/`admin` on the route); this is a second,
 * narrower one in front of it, not a replacement for it.
 */
const ALLOWED_USERNAME = 'gaspar.andrade';

const normalize = (value: string | null | undefined): string =>
  (value ?? '').trim().toLowerCase();

export const canAdministerAppVersions = (
  identity: AppVersionAdminIdentity | null | undefined,
): boolean => {
  if (!identity) return false;

  if (normalize(identity.username) === ALLOWED_USERNAME) return true;

  // The email counts only when its local part is exactly the username AND the domain is ours.
  // Matching on the local part alone would hand the fleet to anyone who can register
  // gaspar.andrade@ at a domain they control.
  const email = normalize(identity.email);
  const [localPart, domain] = email.split('@');
  return localPart === ALLOWED_USERNAME && domain === 'panther.com.br';
};

/**
 * Parse a dotted version into comparable segments, or null when nothing comparable is there.
 *
 * Mirrors condo-manager's appversion.Parse so the warning this screen shows matches the verdict
 * the phones will actually reach: trims space, drops a leading "v", and cuts at the first
 * build-tag separator. Segment count is NOT fixed — Android ships "1.0.24" and iOS build numbers
 * can arrive as "1.0.4.2".
 */
export const parseAppVersion = (raw: string | null | undefined): number[] | null => {
  let s = (raw ?? '').trim();
  if (s.startsWith('v') || s.startsWith('V')) s = s.slice(1);

  const cut = s.search(/[-+ _]/);
  if (cut >= 0) s = s.slice(0, cut);
  if (!s) return null;

  const segments: number[] = [];
  for (const part of s.split('.')) {
    if (!/^\d+$/.test(part)) break; // "1.0.x" is 1.0, so it still sorts below 1.1.0
    segments.push(Number(part));
  }
  return segments.length ? segments : null;
};

/** <0, 0, >0. Missing trailing segments count as zero, so "1.0" equals "1.0.0". */
export const compareAppVersions = (a: string, b: string): number => {
  const left = parseAppVersion(a) ?? [];
  const right = parseAppVersion(b) ?? [];
  const length = Math.max(left.length, right.length);

  for (let i = 0; i < length; i++) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

/**
 * The one check worth blocking a save over: is this floor ahead of what the store serves?
 *
 * A floor the store cannot satisfy sends users to a page offering the build they already have.
 * For a recommended floor that is a nag nobody can clear; for a minimum it is a locked-out fleet
 * with no way forward. App Store review and Play rollout never land together, so each platform
 * must be judged against its own store.
 *
 * Returns null when there is nothing to claim — no floor being set, or an unknown store version
 * (Android has no official endpoint). Silence here means "no opinion", never "all clear".
 */
export const storeLagWarning = (
  floorVersion: string | null | undefined,
  storeVersion: string | null | undefined,
): string | null => {
  if (!parseAppVersion(floorVersion)) return null;
  if (!parseAppVersion(storeVersion)) return null;

  if (compareAppVersions(floorVersion!, storeVersion!) > 0) {
    return `A loja publica ${storeVersion}. Definir ${floorVersion} envia os usuários para uma versão que ainda não está disponível.`;
  }
  return null;
};
